import { Component, Input, OnInit } from '@angular/core';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepsService } from '../../../services/steps.service';
import { Step } from '../../stepper/step';
import { PasswordRetrievalComponent } from '../password-retrieval.component';
import {
	ISendUnlockEmailRequest,
	SendUnlockEmailRequest,
	UnlockAcountEmail,
	ISendUnlockEmailResponse,
	SendUnlockEmailResponse,
} from '../../../classes/unlock-account-email';
import {
	IInitializePasswordRequest,
	InitializePasswordRequest,
	InitializePassword,
} from '../../../classes/initialize-password';
import { ICodeService } from '../../../services/icode.service';
import { UserData, IUserData } from '../../../../globals/classes/userData';
import { PasswordValidator } from '../../../../globals/classes/password-validator';
import { GoogleAnalyticsUtils } from '../../../classes/static/google-analytics-utils';
import { ComponentWithErrors } from '../../../classes/component-with-errors';
import { IVerifyCodeRequest, IUserIDRequest } from '../../../classes/http-requests';
import { IVerifyCodeResponse, ISendSmsOtpResponse } from '../../../classes/http-responses';
import { IErrorDescription } from '@ibank-globals/error';
import { finalize } from 'rxjs/operators/finalize';
import { AppSettings } from '../../../app.settings';

@Component({
	selector: 'email-pass-restore',
	template: require('./email-pass-restore.component.html'),
})
export class EmailPassRestoreComponent extends ComponentWithErrors implements OnInit {
	@Input() public hasBackBtn = true;
	@Input() public isCorporate: boolean = false;
	public userData: IUserData = new UserData();
	public steps: Step[];
	public verfiyCodeRequest: IVerifyCodeRequest = { code: null, userId: null };
	public sendUnlockEmailResponse: ISendUnlockEmailResponse;
	public initRequest: IInitializePasswordRequest = new InitializePasswordRequest();
	public loading: boolean;
	public icode: string;
	public isValidPass = false;
	public isLoginBigModalEnabled: boolean = AppSettings.isLoginBigModalEnabled;
	public title: string;
	public isRebrading = AppSettings.isRebrading;

	public get validFields() {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				return (
					this.userData.userId &&
					this.userData.userId.length > 1 &&
					this.verfiyCodeRequest.code &&
					this.verfiyCodeRequest.code.length >= 6
				);
			case 1:
				return this.isValidPass;
			case 2:
				return this.icodeService.isValidCode(this.icode);
			default:
				return true;
		}
	}

	public get hasBack() {
		return this.stepsService.getCurrentStep != 0;
	}

	constructor(
		private modalService: NgbModal,
		public stepsService: StepsService,
		private activeModal: NgbActiveModal,
		private icodeService: ICodeService
	) {
		super();
	}

	public ngOnInit() {
		this.steps = this.stepsService.getTransactionSteps('EmailPassRestore');
		this.stepsService.setStep(this.steps, 0);
		this.title = this.isCorporate
			? 'Ανάκτηση Password με e-mail'
			: 'Δημιουργία νέου password με e-mail';
		GoogleAnalyticsUtils.sendRetrievePasswordStep1();
		this.initializeSubscriptions();
		this.sendUnlockEmail();
	}

	public sendEmail(resend: boolean) {
		this.sendUnlockEmail(resend);
	}

	public closeModal(): void {
		this.activeModal.close();
	}

	public moveBackStep(steps: Step[]): void {
		if (this.stepsService.getCurrentStep === 0) {
			this.toggleModalDialog();
		} else {
			this.stepsService.moveBackStep(steps);
		}
	}

	public moveStep(): void {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				this.verifyUnlockCode();
				break;
			case 1:
				this.sendInitPass();
				break;
			case 2:
				this.initializePassword();
				break;
			default:
				this.handleError();
		}
	}

	public toggleModalDialog(): void {
		this.activeModal.close();
		let modalRef = this.modalService.open(PasswordRetrievalComponent, {
			windowClass: 'modal-small',
		});
		modalRef.componentInstance.isCorporate = this.isCorporate;
	}

	public sendUnlockEmail(resend: boolean = false) {
		this.loading = true;
		let request: ISendUnlockEmailRequest = new SendUnlockEmailRequest();
		request.userId = this.userData.userId;
		request.resend = resend;
		UnlockAcountEmail.sendUnlockEmail(request)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					this.resolveSendEmailResponse(res);
				},
				(error) => {
					this.handleError();
				}
			);
	}

	public resolveSendEmailResponse(response: ISendUnlockEmailResponse) {
		if (response && response != null) {
			this.sendUnlockEmailResponse = new SendUnlockEmailResponse(response);
			if (this.sendUnlockEmailResponse.succeeded) {
				GoogleAnalyticsUtils.sendRetrievePasswordStep2();
			}
		}
	}

	public verifyUnlockCode() {
		this.loading = true;
		this.verfiyCodeRequest.userId = this.userData.userId;
		UnlockAcountEmail.verifyCode(this.verfiyCodeRequest)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => this.resolveVerifyCodeResponse(res),
				(error) => {
					this.handleError({
						...error,
						modalTitle: 'Κάτι δεν πήγε καλά',
						modalIcon: 'error-x',
						modalAction: 'Ενημερώθηκα',
					});
				}
			);
	}

	private resolveVerifyCodeResponse(response: IVerifyCodeResponse) {
		if (response && response.succeeded) {
			GoogleAnalyticsUtils.sendRetrievePasswordStep3();
			this.stepsService.moveNextStep(this.steps);
			this.setDynatraceEvent('IB_PR_Email_Create_New_Password');
		} else {
			let message = '';
			if (!!response.errorMsg) {
				if (response.errorMsg == 'Invalid username or password.') {
					message = 'Invalid code';
				} else {
					message = response.errorMsg;
				}
			} else {
				message = 'Invalid code';
			}

			const error = {
				desc: message,
				modalTitle: 'Λάθος ηλεκτρονικός κλειδάριθμος',
				modalMessage:
					'Υπάρχει λάθος στον αριθμό του κωδικού επιβεβαίωσης που πληκτρολογήσατε. Παρακαλούμε προσπαθήστε ξανά.',
				modalIcon: 'error-x',
				modalAction: 'Ενημερώθηκα',
			};

			this.handleError(error as IErrorDescription);
		}
	}

	public initializePassword() {
		this.loading = true;
		this.initRequest.otp = this.icode;
		this.initRequest.userID = this.userData.userId;
		InitializePassword.initializePassword(this.initRequest)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					if (res && res.succeeded) {
						GoogleAnalyticsUtils.sendRetrievePasswordStep5();
						this.stepsService.moveNextStep(this.steps);
						this.setDynatraceEvent('IB_PR_Email_Success');
					} else {
						this.handleError();
					}
				},
				(error) => {
					this.handleError(error);
				}
			);
	}

	public sendInitPass(moveNextStep = true) {
		this.loading = true;
		let request: IUserIDRequest = { UserID: this.userData.userId };
		InitializePassword.SendInitOtpPass(request)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					if (res && res.succeeded === true) {
						this.resolveSendInitPassResponse(request, res, moveNextStep);
					}
				},
				(error) => {
					if (this.isCorporate && error.code == '101') {
						let message =
							'Για την ανάκτηση του λογαριασμού σας, παρακαλούμε ενημερώστε τον Νόμιμο Εκπρόσωπο της επιχείρησής σας ώστε να επικοινωνήσει με το τηλεφωνικό μας κέντρο.';

						const error: IErrorDescription = {
							desc: message,
						};

						this.handleError(error);
					} else this.handleError(error);
				}
			);
	}

	public resolveSendInitPassResponse(
		request: IUserIDRequest,
		res: ISendSmsOtpResponse,
		moveNextStep = true
	) {
		this.icodeService.resetTimer();
		this.userData.userId = request.UserID;
		this.userData.hasSMSOtp = res.hasOtp;
		this.userData.maskedPhoneNumber = res.maskedPhoneNumber;
		UserData.update(this.userData);
		GoogleAnalyticsUtils.sendRetrievePasswordStep4();
		if (moveNextStep) {
			this.stepsService.moveNextStep(this.steps);
			this.setDynatraceEvent('IB_PR_Email_Confirm_OTP');
		}
	}

	private initializeSubscriptions() {
		this.mortalize(this.icodeService.code$).subscribe((c) => (this.icode = c));
		this.mortalize(this.icodeService.codeRequested$).subscribe(() => this.sendInitPass(false));
		this.mortalize(UserData.data$).subscribe((userData) => {
			if (!this.userData) return;
			this.userData = userData;
			Object.assign(this.initRequest, { userID: this.userData.userId });
		});
		this.mortalize(PasswordValidator.isValidPassAndConfirm$).subscribe(
			(val) => (this.isValidPass = val)
		);
	}
}
